import { createContext, useContext, useEffect, useState } from "react";
import { AuthApi, getToken, setToken, clearToken } from "../api";
import { logPile } from "../logPile";

type AuthState =
  | { status: "loading" }
  | { status: "loggedOut" }
  | { status: "loggedIn"; userId: string };

const authContext = createContext<{
  auth: AuthState;
  login: (input: {
    username: string;
    password: string;
  }) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}>({
  auth: { status: "loading" },
  login: async () => ({ success: false, error: "not ready" }),
  logout: () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    const attemptRestore = async () => {
      const token = getToken();
      if (!token) {
        setAuth({ status: "loggedOut" });
        return;
      }
      let result: Awaited<ReturnType<typeof AuthApi.getUser>> | undefined;
      try {
        result = await AuthApi.getUser(token);
      } catch (e) {
        // doridium auth unreachable: worth knowing, and the app shouldn't
        // sit on the loading screen forever.
        logPile.captureException(e, {
          message: "session restore: auth unreachable",
        });
      }
      if (result?.success && result?.data?.id) {
        logPile.setUser({ id: result.data.id });
        setAuth({ status: "loggedIn", userId: result.data.id });
      } else {
        // 401 is a routine expiry; anything else is doridium misbehaving.
        if (result && result.status !== 401) {
          logPile.captureMessage("session restore failed", "warning", {
            tags: { status: String(result.status) },
          });
        }
        clearToken();
        setAuth({ status: "loggedOut" });
      }
    };
    attemptRestore();
  }, []);

  const login = async (input: { username: string; password: string }) => {
    logPile.addBreadcrumb({ category: "auth", message: "logged in attempt" });
    const result = await AuthApi.login(input);
    if (!result?.success || !result?.token) {
      logPile.addBreadcrumb({
        category: "auth",
        message: "logged in failed",
        data: result,
      });

      return { success: false, error: result?.message || "Login failed" };
    }
    setToken(result.token);
    const user = await AuthApi.getUser(result.token);
    if (!user?.success || !user?.data?.id) {
      // The token was just accepted, so this is doridium's problem, not a bad password.
      logPile.captureMessage(
        "login: token accepted but user could not be loaded",
        "warning",
        {
          tags: { status: String(user?.status) },
        },
      );
      return { success: false, error: "Could not load user" };
    }
    logPile.setUser({ id: user.data.id });
    logPile.captureEvent("logged in");

    setAuth({ status: "loggedIn", userId: user.data.id });
    return { success: true };
  };

  const logout = () => {
    logPile.captureEvent("logged out");
    logPile.setUser(undefined);
    logPile.reset();
    clearToken();
    setAuth({ status: "loggedOut" });
  };

  return (
    <authContext.Provider value={{ auth, login, logout }}>
      {children}
    </authContext.Provider>
  );
};

export const useAuth = () => useContext(authContext);
